import React, { useState } from "react";
import { motion } from "framer-motion";
import { extractJobData, extractFromPastedJob } from "../utils/openaiApi";
import {
  Briefcase,
  Link2,
  FileText,
  Plus,
  Sparkles,
  Zap,
  Rocket,
  Target,
  Calendar,
  MapPin,
  DollarSign,
  Award,
  X,
  ChevronRight,
} from "lucide-react";

const JobInput = ({ onAddJob, setLoading, setError, loading, error }) => {
  const [url, setUrl] = useState("");
  const [showManualModal, setShowManualModal] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pastedJobDescription, setPastedJobDescription] = useState("");
  const [manualJob, setManualJob] = useState({
    companyName: "",
    jobTitle: "",
    location: "",
    workType: "On-site",
    employmentType: "Full-time",
    salaryRange: "",
    experienceLevel: "Not specified",
    skillsRequired: "",
    jobDescription: "",
    postedDate: new Date().toISOString().split("T")[0],
    applicationDeadline: "",
    jobUrl: "",
    companyWebsite: "",
  });

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleAnalyze = async () => {
    if (!url.trim()) {
      setError("Please enter a job URL");
      return;
    }

    if (!isValidUrl(url)) {
      setError("Please enter a valid job URL");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const minDelay = new Promise((resolve) => setTimeout(resolve, 3000));
      const [jobData] = await Promise.all([extractJobData(url), minDelay]);
      onAddJob(jobData);
      setUrl("");
    } catch (error) {
      console.error("Error analyzing job:", error);
      setError("Failed to analyze job. Please try again or enter manually.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasteJobSubmit = async () => {
    if (!pastedJobDescription.trim()) {
      setError("Please paste a job description");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
      const [jobData] = await Promise.all([
        extractFromPastedJob(pastedJobDescription, url),
        minDelay,
      ]);
      onAddJob(jobData);
      setPastedJobDescription("");
      setUrl("");
      setShowPasteModal(false);
    } catch (error) {
      console.error("Error processing pasted job:", error);
      setError(
        "Failed to process job description. Please try again or enter manually.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualJob.companyName || !manualJob.jobTitle) {
      setError("Company name and job title are required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));
      await minDelay;

      onAddJob({
        ...manualJob,
        skillsRequired: manualJob.skillsRequired
          .split(",")
          .map((skill) => skill.trim())
          .filter(Boolean),
      });

      setManualJob({
        companyName: "",
        jobTitle: "",
        location: "",
        workType: "On-site",
        employmentType: "Full-time",
        salaryRange: "",
        experienceLevel: "Not specified",
        skillsRequired: "",
        jobDescription: "",
        postedDate: new Date().toISOString().split("T")[0],
        applicationDeadline: "",
        jobUrl: "",
      });
      setShowManualModal(false);
    } catch (error) {
      console.error("Error adding manual job:", error);
      setError("Failed to add job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualInputChange = (field, value) => {
    setManualJob((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="relative">
      {/* Main Input Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="backdrop-blur-xl bg-light-card/80 border border-white/20 rounded-3xl p-8 shadow-2xl hover:shadow-primary-500/30 transition-all duration-300"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black font-display text-gray-800 mb-2">
              Add New Job
            </h2>
            <p className="text-gray-600">
              Smart job tracking with AI-powered analysis
            </p>
          </div>
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="text-primary-500"
          >
            <Briefcase className="w-10 h-10" />
          </motion.div>
        </div>

        {/* URL Input */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Link2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste job URL here (LinkedIn, Indeed, etc.)"
              className="w-full pl-12 pr-32 py-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-2xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300 font-display text-gray-800 placeholder-gray-500"
              disabled={loading}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAnalyze}
                disabled={loading || !url.trim()}
                className="bg-gradient-to-r from-primary-500 to-accent-hot text-white rounded-xl px-6 py-2 font-bold font-display transition-all duration-300 shadow-lg hover:shadow-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4"
                    >
                      <Sparkles className="w-4 h-4" />
                    </motion.div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Analyze
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Alternative Options */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          <span className="text-gray-500 text-sm font-medium px-4">or</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <motion.button
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowPasteModal(true)}
            className="backdrop-blur-sm bg-white/40 border border-white/30 rounded-2xl p-4 text-left hover:bg-white/60 transition-all duration-300 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-accent-electric/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <FileText className="w-6 h-6 text-accent-electric" />
              </div>
              <div>
                <h3 className="font-bold font-display text-gray-800">
                  Paste Description
                </h3>
                <p className="text-sm text-gray-600">Copy & paste job text</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-200" />
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowManualModal(true)}
            className="backdrop-blur-sm bg-white/40 border border-white/30 rounded-2xl p-4 text-left hover:bg-white/60 transition-all duration-300 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-accent-neon/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Plus className="w-6 h-6 text-accent-neon" />
              </div>
              <div>
                <h3 className="font-bold font-display text-gray-800">
                  Manual Entry
                </h3>
                <p className="text-sm text-gray-600">Type details manually</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform duration-200" />
            </div>
          </motion.button>
        </div>
      </motion.div>

      {/* Paste Job Modal */}
      {showPasteModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowPasteModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="backdrop-blur-xl bg-light-card/90 border border-white/20 rounded-3xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-black font-display text-gray-800">
                  Paste Job Description
                </h3>
                <p className="text-gray-600">
                  Copy and paste the full job description
                </p>
              </div>
              <button
                onClick={() => setShowPasteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job URL (Optional)
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/job-posting"
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job Description
                </label>
                <textarea
                  value={pastedJobDescription}
                  onChange={(e) => setPastedJobDescription(e.target.value)}
                  placeholder="Paste the complete job description here..."
                  rows={12}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300 resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setShowPasteModal(false)}
                  className="flex-1 px-6 py-3 bg-white/40 border border-white/30 rounded-xl font-bold font-display text-gray-700 hover:bg-white/60 transition-all duration-300"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePasteJobSubmit}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-primary-500 to-accent-hot text-white rounded-xl px-6 py-3 font-bold font-display transition-all duration-300 shadow-lg hover:shadow-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <Sparkles className="w-4 h-4" />
                      </motion.div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4" />
                      Add Job
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Manual Entry Modal */}
      {showManualModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowManualModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="backdrop-blur-xl bg-light-card/90 border border-white/20 rounded-3xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-black font-display text-gray-800">
                  Manual Job Entry
                </h3>
                <p className="text-gray-600">Enter job details manually</p>
              </div>
              <button
                onClick={() => setShowManualModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={manualJob.companyName}
                  onChange={(e) =>
                    handleManualInputChange("companyName", e.target.value)
                  }
                  placeholder="Google, Microsoft, etc."
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={manualJob.jobTitle}
                  onChange={(e) =>
                    handleManualInputChange("jobTitle", e.target.value)
                  }
                  placeholder="Senior Software Engineer"
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Location
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={manualJob.location}
                    onChange={(e) =>
                      handleManualInputChange("location", e.target.value)
                    }
                    placeholder="San Francisco, CA / Remote"
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Work Type
                </label>
                <select
                  value={manualJob.workType}
                  onChange={(e) =>
                    handleManualInputChange("workType", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                >
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="On-site">On-site</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Employment Type
                </label>
                <select
                  value={manualJob.employmentType}
                  onChange={(e) =>
                    handleManualInputChange("employmentType", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Salary Range
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={manualJob.salaryRange}
                    onChange={(e) =>
                      handleManualInputChange("salaryRange", e.target.value)
                    }
                    placeholder="$120k - $180k"
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  value={manualJob.experienceLevel}
                  onChange={(e) =>
                    handleManualInputChange("experienceLevel", e.target.value)
                  }
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                >
                  <option value="Entry-level">Entry-level</option>
                  <option value="Mid-level">Mid-level</option>
                  <option value="Senior-level">Senior-level</option>
                  <option value="Executive">Executive</option>
                  <option value="Not specified">Not specified</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Posted Date
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={manualJob.postedDate}
                    onChange={(e) =>
                      handleManualInputChange("postedDate", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Application Deadline
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Target className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={manualJob.applicationDeadline}
                    onChange={(e) =>
                      handleManualInputChange(
                        "applicationDeadline",
                        e.target.value,
                      )
                    }
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Link2 className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    value={manualJob.jobUrl}
                    onChange={(e) =>
                      handleManualInputChange("jobUrl", e.target.value)
                    }
                    placeholder="https://example.com/job-posting"
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                    Company Website
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Link2 className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="url"
                      value={manualJob.companyWebsite}
                      onChange={(e) =>
                        handleManualInputChange(
                          "companyWebsite",
                          e.target.value,
                        )
                      }
                      placeholder="https://www.company.com"
                      className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                    />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Skills Required
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Award className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={manualJob.skillsRequired}
                    onChange={(e) =>
                      handleManualInputChange("skillsRequired", e.target.value)
                    }
                    placeholder="React, Node.js, Python (comma separated)"
                    className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job Description
                </label>
                <textarea
                  value={manualJob.jobDescription}
                  onChange={(e) =>
                    handleManualInputChange("jobDescription", e.target.value)
                  }
                  placeholder="Full job description..."
                  rows={6}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-6 mt-6 border-t border-white/20">
              <button
                onClick={() => setShowManualModal(false)}
                className="flex-1 px-6 py-3 bg-white/40 border border-white/30 rounded-xl font-bold font-display text-gray-700 hover:bg-white/60 transition-all duration-300"
              >
                Cancel
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleManualSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-primary-500 to-accent-hot text-white rounded-xl px-6 py-3 font-bold font-display transition-all duration-300 shadow-lg hover:shadow-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Sparkles className="w-4 h-4" />
                    </motion.div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Job
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default JobInput;
